// src/components/voice/useGyroscope.ts
//
// Device orientation + mouse parallax unificados detrás de un solo hook.
// En móvil iOS requestPermission() necesita un gesto del usuario — por eso
// devolvemos `needsPermission` + `request()` para el UI.
//
// Output: tilt.x, tilt.y normalizados al rango [-1, 1]. Listo para
// consumir como factor de rotación en CosmicField.

import { useCallback, useEffect, useRef, useState } from 'react';

export type GyroStatus =
  | 'unknown'
  | 'needs_permission'
  | 'granted'
  | 'denied'
  | 'unsupported'
  | 'mouse';

export interface GyroState {
  tilt: { x: number; y: number };     // [-1, 1]
  status: GyroStatus;
  request: () => Promise<void>;
}

// iOS expose requestPermission as a static method on DeviceOrientationEvent.
type IosPermOrientation = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

export function useGyroscope(): GyroState {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [status, setStatus] = useState<GyroStatus>('unknown');
  // Keep latest values in a ref so rAF-driven consumers can read without
  // scheduling re-renders every frame.
  const ref = useRef({ x: 0, y: 0 });

  const update = useCallback((nx: number, ny: number) => {
    // Smoothed follow so the scene doesn't jitter on every sample.
    ref.current.x += (nx - ref.current.x) * 0.12;
    ref.current.y += (ny - ref.current.y) * 0.12;
    setTilt({ x: ref.current.x, y: ref.current.y });
  }, []);

  const attachOrientation = useCallback(() => {
    const handler = (e: DeviceOrientationEvent) => {
      // gamma: left-right [-90, 90], beta: front-back [-180, 180].
      const x = Math.max(-1, Math.min(1, (e.gamma ?? 0) / 45));
      const y = Math.max(-1, Math.min(1, (e.beta ?? 0) / 45));
      update(x, y);
    };
    window.addEventListener('deviceorientation', handler);
    return () => window.removeEventListener('deviceorientation', handler);
  }, [update]);

  useEffect(() => {
    const Ctor = (typeof window !== 'undefined'
      ? (window as any).DeviceOrientationEvent
      : undefined) as IosPermOrientation | undefined;

    // No orientation support at all → fall back to mouse.
    if (!Ctor) {
      setStatus('mouse');
      return;
    }

    // iOS-style permission gate. We cannot call it now (needs user gesture),
    // just advertise the need.
    if (typeof Ctor.requestPermission === 'function') {
      setStatus('needs_permission');
      return;
    }

    // Android / non-iOS browser that ships the API openly.
    const cleanup = attachOrientation();
    setStatus('granted');
    return cleanup;
  }, [attachOrientation]);

  // Desktop / denied → mouse parallax. Run ALWAYS when status ends up in
  // 'mouse' or 'denied' so the scene never feels inert.
  useEffect(() => {
    if (status !== 'mouse' && status !== 'denied') return;
    const handler = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = (e.clientY / window.innerHeight) * 2 - 1;
      update(x, y);
    };
    window.addEventListener('mousemove', handler, { passive: true });
    return () => window.removeEventListener('mousemove', handler);
  }, [status, update]);

  const request = useCallback(async () => {
    const Ctor = (window as any).DeviceOrientationEvent as IosPermOrientation | undefined;
    if (!Ctor?.requestPermission) return;
    try {
      const perm = await Ctor.requestPermission();
      if (perm === 'granted') {
        attachOrientation();
        setStatus('granted');
      } else {
        setStatus('denied');
      }
    } catch {
      setStatus('denied');
    }
  }, [attachOrientation]);

  return { tilt, status, request };
}
